/**
 * Keeps the home agenda fast to reopen.
 *
 * Two things made every visit to Home show the agenda skeleton: the fetch
 * window was built from `Date.now()` at mount, so no two mounts shared a
 * query key, and the calendar caches forget their data a minute after the
 * screen unmounts. The window here snaps to local midnights so the key
 * repeats within a day, and the snapshot below retains the last agenda
 * source events per user (in memory and in sessionStorage) so a reopened
 * Home paints from them while the live queries refresh behind.
 */
import type { WorkspaceAgendaSourceEvent } from '@rudi/shared/agenda/workspaceAgenda';

const AGENDA_BACK_DAYS = 7;
const AGENDA_FORWARD_DAYS = 31;
/** Events that ended more than a day before the snapshot was taken are dropped. */
const SNAPSHOT_KEEP_PAST_MS = 24 * 60 * 60 * 1000;
const SNAPSHOT_MAX_EVENTS = 60;
const STORAGE_PREFIX = 'rudi-agenda-snapshot:';

export interface WorkspaceAgendaSnapshot {
  events: WorkspaceAgendaSourceEvent[];
  /** `configId -> accountId:providerCalendarId`, for the hidden-calendar filter. */
  visibilityEntries: [string, string][];
  at: number;
}

/** A day-aligned window, so remounts within the same day reuse cached ranges. */
export function agendaFetchWindow(now: Date): { timeMin: Date; timeMax: Date } {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const timeMin = new Date(midnight);
  timeMin.setDate(timeMin.getDate() - AGENDA_BACK_DAYS);
  const timeMax = new Date(midnight);
  timeMax.setDate(timeMax.getDate() + AGENDA_FORWARD_DAYS);
  return { timeMin, timeMax };
}

const memory = new Map<string, WorkspaceAgendaSnapshot>();

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function eventEndMs(event: WorkspaceAgendaSourceEvent): number {
  const end = event.end ? Date.parse(event.end) : NaN;
  if (!Number.isNaN(end)) return event.allDay ? end + SNAPSHOT_KEEP_PAST_MS : end;
  const start = Date.parse(event.start);
  return Number.isNaN(start) ? 0 : start + SNAPSHOT_KEEP_PAST_MS;
}

/** Only the fields the agenda builder reads, so the stored copy stays small. */
function slimEvent(event: WorkspaceAgendaSourceEvent): WorkspaceAgendaSourceEvent {
  const props = event.extendedProps;
  return {
    id: event.id,
    title: event.title,
    start: event.start,
    ...(event.end ? { end: event.end } : {}),
    allDay: event.allDay,
    ...(event.isExternalEvent ? { isExternalEvent: true } : {}),
    ...(props
      ? {
          extendedProps: {
            ...(props.isExternalEvent ? { isExternalEvent: true } : {}),
            ...(props.accountId ? { accountId: props.accountId } : {}),
            ...(props.calendarId ? { calendarId: props.calendarId } : {}),
            ...(props.syncConfigId ? { syncConfigId: props.syncConfigId } : {}),
          },
        }
      : {}),
  };
}

export function rememberAgendaSnapshot(
  userId: string,
  events: ReadonlyArray<WorkspaceAgendaSourceEvent>,
  configIdToVisibilityKey: ReadonlyMap<string, string>,
  now: Date = new Date()
): WorkspaceAgendaSnapshot {
  const cutoff = now.getTime() - SNAPSHOT_KEEP_PAST_MS;
  const kept = events
    .filter(event => eventEndMs(event) >= cutoff)
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start))
    .slice(0, SNAPSHOT_MAX_EVENTS)
    .map(slimEvent);
  const snapshot: WorkspaceAgendaSnapshot = {
    events: kept,
    visibilityEntries: [...configIdToVisibilityKey.entries()],
    at: now.getTime(),
  };
  memory.set(userId, snapshot);
  try {
    storage()?.setItem(STORAGE_PREFIX + userId, JSON.stringify(snapshot));
  } catch {
    // Quota or privacy mode: the in-memory copy still covers this session.
  }
  return snapshot;
}

export function readAgendaSnapshot(userId: string): WorkspaceAgendaSnapshot | null {
  const cached = memory.get(userId);
  if (cached) return cached;
  try {
    const raw = storage()?.getItem(STORAGE_PREFIX + userId);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !Array.isArray((parsed as WorkspaceAgendaSnapshot).events) ||
      !Array.isArray((parsed as WorkspaceAgendaSnapshot).visibilityEntries)
    ) {
      return null;
    }
    const snapshot = parsed as WorkspaceAgendaSnapshot;
    memory.set(userId, snapshot);
    return snapshot;
  } catch {
    return null;
  }
}

/** Drop retained snapshots, for sign-out and tests. */
export function clearAgendaSnapshots(): void {
  memory.clear();
  const store = storage();
  if (!store) return;
  try {
    for (const key of [...Array(store.length).keys()].map(index => store.key(index))) {
      if (key?.startsWith(STORAGE_PREFIX)) store.removeItem(key);
    }
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
